import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState, useRef, useCallback, } from 'react';
import { Indicator } from './Indicator';
import { InputContainer } from './InputContainer';
import { Dropdown } from './Dropdown';
import { filterOptions, containsNode, formInputSelectValueToOption, formInputSelectOptionToValue, } from './utils';
import { useFormInputStyles } from '../FormInputText/FormInputText';
import * as styles from './BaseSelect.module.scss';
export const BaseSelect = React.forwardRef(({ id, labelId, multi, options, value, placeholder, onChange, ...props }, ref) => {
    const controlRef = useRef(null);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const [selections, setSelections] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState();
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    useEffect(() => {
        if (value) {
            const updates = formInputSelectValueToOption(options, value);
            setSelections(updates);
        }
    }, [value, setSelections, options]);
    const handleClickOutside = useCallback((event) => {
        const isInDropdown = containsNode(dropdownRef.current, event.target);
        const isInControl = containsNode(controlRef.current, event.target);
        if (isInControl)
            return;
        if (isDropdownOpen && !isInDropdown)
            closeDropdown();
    }, [isDropdownOpen]);
    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [handleClickOutside]);
    const focusInput = () => inputRef.current?.focus();
    const blurInput = () => inputRef.current?.blur();
    const clearInput = () => setInputValue('');
    const openDropdown = () => setIsDropdownOpen(true);
    const closeDropdown = () => setIsDropdownOpen(false);
    const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);
    const clearValue = () => {
        setSelections([]);
        onChange?.([]);
    };
    const addValue = (value) => {
        const option = filterOptions(options, inputValue).find(option => option.value === value);
        if (option) {
            const updates = [...selections, option];
            setSelections(updates);
            const values = formInputSelectOptionToValue(updates, true);
            onChange?.(values);
        }
    };
    const removeValue = (value) => {
        const updates = filterOptions(selections, inputValue).filter(selection => selection.value !== value);
        setSelections(updates);
        const values = formInputSelectOptionToValue(updates, true);
        onChange?.(values);
    };
    const toggleValue = (value) => {
        const selection = filterOptions(selections, inputValue).find(selection => selection.value === value);
        if (selection) {
            removeValue(value);
        }
        else {
            addValue(value);
        }
    };
    const backspaceValue = () => {
        if (selections.length > 0) {
            const selection = selections[selections.length - 1];
            removeValue(selection.value);
        }
    };
    const handleControlClick = () => {
        focusInput();
        toggleDropdown();
    };
    const handleRemoveValue = (value) => {
        removeValue(value);
        focusInput();
    };
    const handleClearValue = () => {
        clearValue();
        focusInput();
    };
    const handleInputChange = (event) => {
        const newInputValue = event.target.value;
        if (!isDropdownOpen)
            openDropdown();
        setInputValue(newInputValue);
    };
    const handleKeyDown = (event) => {
        const filteredOptions = filterOptions(options, inputValue);
        switch (event.key) {
            case 'Backspace':
                if (!multi)
                    return;
                if (!inputValue)
                    backspaceValue();
                break;
            case 'Escape':
                if (isDropdownOpen)
                    closeDropdown();
                if (inputValue)
                    clearInput();
                break;
            case 'ArrowUp':
                if (!isDropdownOpen) {
                    setHighlightedIndex(0);
                    openDropdown();
                    return;
                }
                const prevIndex = highlightedIndex <= 0
                    ? filteredOptions.length - 1
                    : highlightedIndex - 1;
                setHighlightedIndex(prevIndex);
                break;
            case 'ArrowDown':
                if (!isDropdownOpen) {
                    setHighlightedIndex(0);
                    openDropdown();
                    return;
                }
                const nextIndex = highlightedIndex >= filteredOptions.length - 1
                    ? 0
                    : highlightedIndex + 1;
                setHighlightedIndex(nextIndex);
                break;
            case 'Enter':
                const option = filteredOptions[highlightedIndex];
                if (multi) {
                    toggleValue(option.value);
                    clearInput();
                }
                else {
                    handleSingleOptionClick(option);
                }
                break;
            default:
                break;
        }
    };
    const handleItemClick = (event) => {
        event.stopPropagation();
        focusInput();
    };
    const handleOptionChange = (option) => {
        toggleValue(option.value);
        clearInput();
        focusInput();
    };
    const handleSingleOptionClick = (option) => {
        const updates = [option];
        setSelections(updates);
        clearInput();
        blurInput();
        closeDropdown();
        const values = formInputSelectOptionToValue(updates);
        onChange?.(values);
    };
    const handleOptionMouseEnter = (index) => setHighlightedIndex(index);
    return (_jsxs("div", { ref: ref, id: id, className: styles.select, children: [_jsxs("div", { role: "combobox", "aria-labelledby": labelId ?? '', "aria-expanded": isDropdownOpen, "aria-controls": "select-control", ref: controlRef, id: "select-control", onClick: handleControlClick, className: useFormInputStyles({
                    className: styles.control,
                }), ...props, children: [_jsx(InputContainer, { labelId: labelId ?? '', multi: multi, inputRef: inputRef, selections: selections, inputValue: inputValue, placeholder: placeholder, onClickItem: handleItemClick, onKeyDown: handleKeyDown, onChange: handleInputChange, onRemoveValue: handleRemoveValue }), _jsx(Indicator, { multi: multi, selections: selections, onClearValue: handleClearValue, onClickToggle: handleControlClick })] }), isDropdownOpen && (_jsx("div", { id: "form-select-control", ref: dropdownRef, className: styles.menu, children: _jsx(Dropdown, { multi: multi, options: options, inputValue: inputValue, selections: selections, highlightedIndex: highlightedIndex, onChange: handleOptionChange, onMouseEnter: handleOptionMouseEnter, onClick: handleSingleOptionClick }) }))] }));
});
